import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { EduJoyride } from '@edulastic/common'
import { tutorials } from '@edulastic/constants'
import { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride'
import {
  getNewDashboardJoyrideVisible,
  getTotalAssignmentCount,
  isTeacherDashboardLoading,
} from '../ducks'
import { getUserId } from '../../src/selectors/user'
import {
  isTestCreationLifecycleBannerDismissed,
  newDashboardOverviewSteps,
  newDashboardOverviewStepsWithoutLifecycleBanner,
} from '../constants'
import { setViewedTutorialsAction } from '../../../tutorials/tutorialReducer'

const CLASSES_TAB_TARGET = '[data-joyride-id="classesTab"]'

const NewDashboardJoyride = ({
  showJoyride,
  userId,
  setViewedTutorials,
  totalAssignmentCount,
  isLoading,
}) => {
  const [joyrideRun, setJoyrideRun] = useState(false)
  const [joyrideStepIndex, setJoyrideStepIndex] = useState(0)
  const [joyrideClosed, setJoyrideClosed] = useState(false)
  const lockedStepsRef = useRef(null)

  const hideLifecycleStep =
    isLoading ||
    totalAssignmentCount >= 2 ||
    isTestCreationLifecycleBannerDismissed(userId)

  const joyrideSteps = useMemo(() => {
    if (!showJoyride) {
      lockedStepsRef.current = null
      return []
    }
    if (!lockedStepsRef.current) {
      lockedStepsRef.current = hideLifecycleStep
        ? newDashboardOverviewStepsWithoutLifecycleBanner
        : newDashboardOverviewSteps
    }
    return lockedStepsRef.current
  }, [hideLifecycleStep, showJoyride])

  const classesTabStepIndex = hideLifecycleStep ? 0 : 1
  const gridListViewStepIndex = hideLifecycleStep ? 1 : 2

  useEffect(() => {
    if (!showJoyride) {
      lockedStepsRef.current = null
      setJoyrideStepIndex(0)
      setJoyrideClosed(false)
      setJoyrideRun(false)
      return
    }
    setJoyrideRun(true)
  }, [showJoyride])

  const markOverviewComplete = useCallback(() => {
    setViewedTutorials([tutorials.newDashboardOverviewSteps])
  }, [setViewedTutorials])

  const joyrideCallback = useCallback(
    (data) => {
      if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(data.type)) {
        const nextStepIndex =
          data.index + (data.action === ACTIONS.PREV ? -1 : 1)
        const element = document.querySelector(
          joyrideSteps[nextStepIndex]?.target
        )
        setJoyrideRun(false)

        element?.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        })
        if (data.action === ACTIONS.CLOSE) {
          setJoyrideClosed(true)
        }
        if (data.action !== ACTIONS.CLOSE && !joyrideClosed) {
          setTimeout(() => {
            setJoyrideStepIndex(nextStepIndex)
            setJoyrideRun(true)
          }, 400)
        }
      }

      if (!joyrideClosed) {
        const shouldActivateClassesTab =
          data.action === ACTIONS.UPDATE &&
          (data.index === classesTabStepIndex ||
            data.index === gridListViewStepIndex)
        if (shouldActivateClassesTab) {
          document.querySelector(CLASSES_TAB_TARGET)?.click()
        }
      }

      if (
        data.action === ACTIONS.CLOSE ||
        (data.lifecycle === LIFECYCLE.COMPLETE &&
          data.action === ACTIONS.NEXT &&
          data.index === joyrideSteps.length - 1)
      ) {
        setJoyrideRun(false)
        markOverviewComplete()
      }
    },
    [
      classesTabStepIndex,
      gridListViewStepIndex,
      joyrideClosed,
      joyrideSteps,
      markOverviewComplete,
    ]
  )

  if (!showJoyride || !joyrideSteps.length) {
    return null
  }

  return (
    <EduJoyride
      steps={joyrideSteps}
      locale={{
        close: 'Done',
        last: 'Done',
      }}
      disableOverlayClose
      showProgress={joyrideSteps.length > 1}
      options={{ zIndex: 1015 }}
      callback={joyrideCallback}
      continuous
      disableScrolling
      stepIndex={joyrideStepIndex}
      run={joyrideRun}
      showSkipButton
    />
  )
}

export default connect(
  (state) => ({
    showJoyride: getNewDashboardJoyrideVisible(state),
    userId: getUserId(state),
    totalAssignmentCount: getTotalAssignmentCount(state),
    isLoading: isTeacherDashboardLoading(state),
  }),
  {
    setViewedTutorials: setViewedTutorialsAction,
  }
)(NewDashboardJoyride)
